import React from 'react';
import {
  ArrowRightOutlined,
  CheckCircleOutlined,
  FlagOutlined,
  LoadingOutlined,
  SmileOutlined,
} from '@ant-design/icons';
import clsx from 'clsx';
import { Avatar, Button, Modal } from 'antd';
import { Form, useEffectWithPrev } from '@owl-frontend/components';
import { Locale } from '../../context/AppProvider';
import useFeedbackHook from './useFeedbackHook';
import styles from './FeedbackComponents.module.scss';

interface FeedbackFormProps {
  formType: string;
  placeholder?: string;
  open: boolean;
  onClose(): void;
}

const FeedbackForm: Form.MFC<FeedbackFormProps, { feedback: string }> = ({
  formType,
  placeholder,
  open,
  onClose,
  // RFF
  invalid,
  pristine,
  form,
  handleSubmit,
}) => {
  const { messages } = React.useContext(Locale);
  const sendFeedback = React.useCallback(() => {
    handleSubmit();
    form.restart();
  }, [form, handleSubmit]);

  return (
    <Modal
      classNames={{
        mask: styles.mask,
      }}
      title={messages[`feedback.${formType}.description`]}
      open={open}
      onCancel={onClose}
      okText={messages[`feedback.${formType}.submit`]}
      okButtonProps={{
        iconPlacement: 'end',
        icon: <ArrowRightOutlined />,
        onClick: sendFeedback,
        disabled: invalid || pristine,
      }}
      cancelButtonProps={{
        onClick: onClose,
      }}
      destroyOnHidden
    >
      <form onSubmit={sendFeedback}>
        <Form.InputManaged
          name="feedback"
          placeholder={
            placeholder ?? messages[`feedback.${formType}.placeholder`]
          }
          rows={7}
        />
      </form>
    </Modal>
  );
};

export const FeedbackFormManaged = Form.manage(FeedbackForm);

interface FeedbackComponentProps {
  type: string;
  formType: string;
  button: React.ReactNode;
  dossier_id?: string;
  data?: Record<string, string>;
  placeholder?: string;
}

const GenericFeedbackComponent: React.FC<
  FeedbackComponentProps & React.PropsWithChildren
> = ({
  children,
  type,
  formType,
  button,
  dossier_id,
  data = {},
  placeholder,
}) => {
  const [feedbackButton, setFeedbackButton] = React.useState(button);
  const [isSubmitted, setIsSubmitted] = React.useState(false);
  const [isOpen, setIsOpen] = React.useState(false);
  const [
    {
      actions: { addFeedback },
    },
    handler,
  ] = useFeedbackHook();

  const handleFeedbackSubmit = React.useCallback(
    (feedback) => {
      setIsSubmitted(true);
      setFeedbackButton(<LoadingOutlined />);
      handler.addFeedback({
        ...feedback,
        type,
        data,
        dossier_id,
      });
      setIsOpen(false);
    },
    [dossier_id, type, data, handler]
  );

  useEffectWithPrev(
    (prevStatus) => {
      if (prevStatus !== 'pending' || !isSubmitted) {
        return;
      }
      if (addFeedback.status === 'fulfilled') {
        setFeedbackButton(<CheckCircleOutlined />);
        setTimeout(() => {
          setFeedbackButton(button);
          setIsSubmitted(false);
        }, 5000);
      }
    },
    [addFeedback.status, button, isSubmitted]
  );

  return (
    <>
      <Button
        type="text"
        className={styles.popover}
        onClick={() => {
          setIsOpen(true);
        }}
      >
        <Avatar
          className={clsx(
            styles.feedbackButton,
            formType === 'general' && styles.generalFeedbackButton,
            formType !== 'general' && styles.specificFeedbackButton,
            formType !== 'general' && isOpen && styles.active
          )}
          size={28}
          icon={feedbackButton}
        />
        {children}
      </Button>
      <FeedbackFormManaged
        formType={formType}
        onSubmit={handleFeedbackSubmit}
        placeholder={placeholder}
        open={isOpen}
        onClose={() => {
          setIsOpen(false);
        }}
      />
    </>
  );
};

export const GeneralFeedbackComponent: React.FC<
  {
    dossier_id: string;
  } & React.PropsWithChildren
> = ({ dossier_id, children }) => {
  return (
    <GenericFeedbackComponent
      type="general"
      formType="general"
      dossier_id={dossier_id}
      button={<SmileOutlined style={{ fontSize: '14px' }} />}
    >
      {children}
    </GenericFeedbackComponent>
  );
};

interface SectionFeedbackComponentProps {
  type: string;
  dossier_id: string;
  data?: Record<string, string>;
  placeholder?: string;
}

export const SectionFeedbackComponent: React.FC<
  SectionFeedbackComponentProps
> = ({ type, dossier_id, data = {}, placeholder }) => {
  return (
    <GenericFeedbackComponent
      formType="specific"
      type={type}
      dossier_id={dossier_id}
      data={data}
      button={<FlagOutlined style={{ fontSize: '16px' }} />}
      placeholder={placeholder}
    />
  );
};
